/**
 * 10.5Y — TELA DE MISSÕES (📜). Antes as missões só apareciam numa faixa no HUD e não dava para
 * abrir a lista. Aqui ficam as 3 ativas com progresso, PRÊMIO de cada uma e o total já cumprido.
 * A recompensa é paga automaticamente ao completar (não tem botão de "coletar" para esquecer).
 */
import { i18n } from "../i18n/i18n";

export interface MissionRow {
  id: string;
  name: string;
  progress: number;
  target: number;
  coins: number;
  xp: number;
  shards: number;
  done: number; // quantas vezes essa missão já foi cumprida
  /** 10.6F — cumprida e esperando o jogador tocar em Coletar. */
  ready: boolean;
}
/** 10.6M — bloco da missão DIÁRIA, no topo da tela. */
export interface DailyRow {
  name: string;
  progress: number;
  target: number;
  coins: number;
  xp: number;
  shards: number;
  ready: boolean;
  claimed: boolean;
  done: number;
  /** 10.9BE — oferta de DOBRAR por anúncio ("ad") ou direto pro VIP ("vip"); undefined = sem oferta hoje */
  adDouble?: "ad" | "vip";
}

export interface MissionsData {
  daily?: DailyRow;
  rows: MissionRow[];
  totalDone: number;
  contractSeconds: number; // 0 = sem Contrato de Caça ativo
}

const ICON: Record<string, string> = {
  m_kill: "⚔️",
  m_hunt: "🎯",
  m_elite: "👑",
  m_chest: "🎁",
  m_explore: "🗺️",
  m_boss: "☠️",
};

export class MissionsScreen {
  private readonly root: HTMLDivElement;
  private readonly listEl: HTMLElement;
  private readonly dailyEl: HTMLElement;
  private provider: (() => MissionsData) | null = null;

  constructor(uiRoot: HTMLElement, private readonly onClose: () => void, private readonly onClaim?: (id: string) => void) {
    this.root = document.createElement("div");
    this.root.className = "overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel ms-panel void-panel">
        <div class="ms-head"><h2 class="ms-title"></h2><button class="btn ms-x" data-a="close">✕</button></div>
        <div class="ms-sub"></div>
        <div class="ms-daily"></div>
        <div class="ms-list"></div>
        <div class="ms-foot"></div>
        <button class="btn ms-back" data-a="close2" hidden></button><!-- 10.9FE: o ✕ do topo basta -->
      </div>`;
    uiRoot.appendChild(this.root);
    this.listEl = this.root.querySelector(".ms-list")!;
    this.dailyEl = this.root.querySelector(".ms-daily")!;
    this.dailyEl.addEventListener("click", (ev) => {
      const b = (ev.target as HTMLElement).closest<HTMLElement>("[data-claim]");
      if (b) this.onClaim?.(b.dataset.claim === "__daily_ad" ? "__daily_ad" : "__daily"); // 10.9BE
    });
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.onClose());
    this.root.querySelector('[data-a="close2"]')!.addEventListener("click", () => this.onClose());
    // 10.6F — botão COLETAR por missão pronta (delegado: a lista é redesenhada a cada render)
    this.listEl.addEventListener("click", (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>("[data-claim]");
      if (!btn) return;
      this.onClaim?.(btn.dataset.claim!);
    });
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  open(provider: () => MissionsData): void {
    this.provider = provider;
    this.root.hidden = false;
    this.render();
  }
  hide(): void {
    this.root.hidden = true;
  }
  refresh(): void {
    if (!this.root.hidden) this.render();
  }

  private render(): void {
    if (!this.provider) return;
    const d = this.provider();
    this.root.querySelector(".ms-title")!.textContent = i18n.t("mission.title");
    this.root.querySelector(".ms-back")!.textContent = i18n.t("menu.back");
    this.root.querySelector(".ms-sub")!.textContent = i18n.t("mission.sub");
    // 10.6M — diária no topo: é o objetivo do dia, tem que ser a primeira coisa que se vê
    const dl = d.daily;
    this.dailyEl.innerHTML = dl
      ? `<div class="ms-card daily${dl.ready ? " ready" : ""}${dl.claimed ? " claimed" : ""}">
           <div class="ms-top"><span class="ms-ic">🗓️</span><span class="ms-name">${i18n.t("daily.title")}: ${dl.name}</span><span class="ms-count">${dl.progress}/${dl.target}</span></div>
           <div class="ms-bar"><i style="width:${Math.round(Math.min(1, dl.target ? dl.progress / dl.target : 0) * 100)}%"></i></div>
           <div class="ms-rw"><span class="mr-coin">+${dl.coins} 🪙</span> <span class="mr-xp">+${dl.xp} XP</span>${dl.shards > 0 ? ` <span class="mr-sh">+${dl.shards} 🔷</span>` : ""}<span class="ms-done">${i18n.t("daily.streak", { n: dl.done })}</span></div>
           ${dl.claimed ? `<div class="ms-daily-ok">${i18n.t("daily.claimed")}</div>` : dl.ready ? `<div class="ms-claim-row"><button class="btn primary ms-claim" data-claim="__daily">${i18n.t("mission.claim")}</button>${dl.adDouble ? `<button class="btn ms-claim ms-claim-ad" data-claim="__daily_ad">${i18n.t(dl.adDouble === "vip" ? "ads.dailyDoubleVip" : "ads.dailyDouble")}</button>` : ""}</div>` : ""}
         </div>`
      : "";
    this.listEl.innerHTML = d.rows
      .map((m) => {
        const pct = Math.max(0, Math.min(1, m.target > 0 ? m.progress / m.target : 0));
        const ready = m.ready;
        const rw: string[] = [`<span class="mr-coin">+${m.coins} 🪙</span>`, `<span class="mr-xp">+${m.xp} XP</span>`];
        if (m.shards > 0) rw.push(`<span class="mr-sh">+${m.shards} 🔷</span>`);
        const claim = ready ? `<button class="btn primary ms-claim" data-claim="${m.id}">${i18n.t("mission.claim")}</button>` : "";
        return `<div class="ms-card${ready ? " ready" : ""}">
          <div class="ms-top">
            <span class="ms-ic">${ICON[m.id] ?? "📜"}</span>
            <span class="ms-name">${m.name}</span>
            <span class="ms-count">${m.progress}/${m.target}</span>
          </div>
          <div class="ms-bar"><i style="width:${Math.round(pct * 100)}%"></i></div>
          <div class="ms-rw">${rw.join(" ")}${m.done > 0 ? `<span class="ms-done">${i18n.t("mission.doneTimes", { n: m.done })}</span>` : ""}</div>
          ${claim}
        </div>`;
      })
      .join("");
    const contract = d.contractSeconds > 0
      ? `<div class="ms-contract">📜 ${i18n.t("shop.item.contract.name")} · ${Math.floor(d.contractSeconds / 60)}:${String(Math.ceil(d.contractSeconds) % 60).padStart(2, "0")}</div>`
      : "";
    this.root.querySelector(".ms-foot")!.innerHTML = `${contract}<div class="ms-total">${i18n.t("mission.total", { n: d.totalDone })}</div>`;
  }
}
